/**
 * Shared keyboard navigation hook for autocomplete/dropdown components.
 * Manages selectedIndex state, native keydown listener, wrap/clamp,
 * scroll-into-view, and index reset on dependency changes.
 */

import { useState, useEffect, useRef } from 'react';

export interface UseAutocompleteKeyboardConfig {
  itemCount: number;
  onSelect: (index: number) => void;
  onClose?: () => void;
  enabled?: boolean;
  wrapAround?: boolean;
  resetDeps?: unknown[];
  scrollIntoView?: boolean | string;
  selectKeys?: string[];
  listenerTarget?: 'window' | 'document';
  additionalKeys?: Record<string, (e: KeyboardEvent) => void>;
}

export interface UseAutocompleteKeyboardReturn {
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
}

export function useAutocompleteKeyboard({
  itemCount,
  onSelect,
  onClose,
  enabled = true,
  wrapAround = true,
  resetDeps = [],
  scrollIntoView: scrollConfig = true,
  selectKeys = ['Enter'],
  listenerTarget = 'window',
  additionalKeys,
}: UseAutocompleteKeyboardConfig): UseAutocompleteKeyboardReturn {
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Keep callbacks in refs to avoid re-attaching the listener on every render
  const onSelectRef = useRef(onSelect);
  const onCloseRef = useRef(onClose);
  const additionalKeysRef = useRef(additionalKeys);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => { additionalKeysRef.current = additionalKeys; }, [additionalKeys]);

  // Clamp selectedIndex when itemCount shrinks below it
  useEffect(() => {
    if (itemCount > 0) {
      setSelectedIndex(prev => prev >= itemCount ? itemCount - 1 : prev);
    }
  }, [itemCount]);

  // Reset selectedIndex when resetDeps change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setSelectedIndex(0); }, resetDeps);

  // Scroll selected item into view
  useEffect(() => {
    if (!scrollConfig || itemCount === 0) return;
    const selector = typeof scrollConfig === 'string'
      ? scrollConfig.replace('${index}', String(selectedIndex))
      : `[data-index="${selectedIndex}"]`;
    const el = document.querySelector(selector);
    if (el) {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex, scrollConfig, itemCount]);

  // Native keydown listener
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (itemCount === 0) {
        if (e.key === 'Escape' && onCloseRef.current) {
          e.preventDefault();
          onCloseRef.current();
        }
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev =>
          wrapAround
            ? (prev + 1) % itemCount
            : Math.min(prev + 1, itemCount - 1)
        );
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev =>
          wrapAround
            ? (prev - 1 + itemCount) % itemCount
            : Math.max(prev - 1, 0)
        );
        return;
      }

      if (selectKeys.includes(e.key)) {
        e.preventDefault();
        onSelectRef.current(selectedIndex);
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current?.();
        return;
      }

      if (additionalKeysRef.current?.[e.key]) {
        additionalKeysRef.current[e.key](e);
      }
    };

    const target = listenerTarget === 'document' ? document : window;
    target.addEventListener('keydown', handleKeyDown as EventListener);
    return () => target.removeEventListener('keydown', handleKeyDown as EventListener);
  }, [enabled, itemCount, wrapAround, selectKeys, listenerTarget, selectedIndex]);

  return { selectedIndex, setSelectedIndex };
}
